import { type FC } from 'react';

import { CompassLoader } from '@vakantiesnl/components/src/atoms/CompassLoader';
import { NoBookingsBlock } from '@vakantiesnl/components/src/atoms/NoBookingsBlock';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { BookingAccommodationCard } from '@vakantiesnl/components/src/molecules/BookingAccommodationCard';
import { UpcomingOfferSummary } from '@vakantiesnl/components/src/molecules/OfferSummary/UpcomingOfferSummary';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useGetUserBookings } from '@vakantiesnl/services/src/hooks/queries';
import { type ContentTemplate } from '@vakantiesnl/types/src';
import dayjs from 'dayjs';

import useStyles from './BookingsOverview.style';

type Props = {
	content: ContentTemplate;
};

export const BookingsOverview: FC<Props> = ({ content }) => {
	const { isLoadingBookings, upcomingBookings, pastAndCancelledBookings } = useGetUserBookings();
	const { classes } = useStyles();
	const microCopies = useMicroCopyContext();

	return (
		<>
			<Typography variant="heading2Xl" as="h2">
				{content.title}
			</Typography>

			<div className={classes.itemsBlock}>
				<Typography variant="headingXl" as="h3" data-cy="upcoming-bookings-title">
					{microCopies['user.bookings.upcomingBookingsTitle']}
				</Typography>
				<div className={classes.itemContentBlock}>
					{isLoadingBookings && <CompassLoader />}
					{!!upcomingBookings.length && (
						<ul className={classes.upcomingBookingsGrid} data-cy="upcoming-bookings-grid">
							{upcomingBookings.map((b) => (
								<li key={b.bookingNumber}>
									<UpcomingOfferSummary
										microCopies={microCopies}
										accommodation={b.accommodation}
										departureDate={dayjs(b.departureDate, 'YYYY-MM-DD').format()}
										arrivalDate={dayjs(b.returnDate, 'YYYY-MM-DD').format()}
										numberOfPassengers={b.numberOfPassengers}
										bookingNumber={String(b.bookingNumber)}
										isAccoOnly={b.isAccoOnly}
									/>
								</li>
							))}
						</ul>
					)}
					{!isLoadingBookings && upcomingBookings.length === 0 && <NoBookingsBlock />}
				</div>
			</div>

			{(isLoadingBookings || !!pastAndCancelledBookings.length) && (
				<div className={classes.itemsBlock}>
					<Typography variant="headingXl" as="h3" data-cy="past-bookings-title">
						{microCopies['user.bookings.pastCancelledBookingsTitle']}
					</Typography>
					<div className={classes.itemContentBlock}>
						{isLoadingBookings && <CompassLoader />}
						{!!pastAndCancelledBookings.length && (
							<ul className={classes.pastCancelledBookingsGrid} data-cy="past-bookings-grid">
								{pastAndCancelledBookings.map((b, i) => (
									<li key={b.bookingNumber}>
										<BookingAccommodationCard
											accommodation={b.accommodation}
											arrivalDate={dayjs(b.returnDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
											bookingNumber={b.bookingNumber}
											departureDate={dayjs(b.departureDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
											isCancelled={b.isCancelled}
											position={i}
										/>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			)}
		</>
	);
};
